
import { PowerBICourse } from './types';

export const POWERBI_FUNDAMENTALS_COURSE: PowerBICourse = {
  id: 'powerbi-fundamentals',
  title: 'Power BI Fundamentals',
  description: 'Master the basics of Microsoft Power BI for data visualization and business intelligence.',
  level: 'fundamentals',
  totalDuration: '12-15 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Basic understanding of data concepts',
    'Familiarity with Microsoft Excel',
    'Basic business analysis knowledge'
  ],
  learningOutcomes: [
    'Navigate Power BI Desktop interface effectively',
    'Connect to various data sources',
    'Create basic visualizations and reports',
    'Understand data modeling concepts',
    'Share reports through Power BI Service'
  ],
  modules: [
    {
      id: 'powerbi-intro',
      title: 'Introduction to Power BI',
      description: 'Get started with Power BI and understand its ecosystem.',
      content: `
# Introduction to Power BI

## What is Power BI?

Power BI is Microsoft's business analytics tool that provides interactive visualizations and business intelligence capabilities with an interface simple enough for end users to create their own reports and dashboards.

## Power BI Components

### Power BI Desktop
- Desktop application for creating reports
- Data modeling and transformation capabilities
- Rich visualization tools

### Power BI Service
- Cloud-based service for sharing reports
- Collaboration and distribution platform
- Real-time dashboard capabilities

### Power BI Mobile
- Mobile apps for iOS and Android
- Offline viewing capabilities
- Touch-optimized interface

## Getting Started

### Installation and Setup
1. Download Power BI Desktop from Microsoft
2. Sign up for Power BI Service account
3. Understand licensing options

### Interface Overview
- Report view
- Data view
- Model view
- Ribbon and panes explanation

## Key Benefits

- **Self-service analytics**: Enable business users to create their own reports
- **Real-time insights**: Connect to live data sources
- **Collaboration**: Share and collaborate on reports and dashboards
- **Scalability**: From personal use to enterprise deployment
      `,
      duration: '45 minutes',
      difficulty: 'beginner',
      order: 1,
      estimatedTime: '45 minutes',
      objectives: [
        'Understand Power BI ecosystem and components',
        'Navigate Power BI Desktop interface',
        'Recognize different view modes and their purposes',
        'Identify key benefits of Power BI for business intelligence'
      ],
      quiz: {
        questions: [
          {
            id: 'powerbi-intro-q1',
            question: 'Which Power BI component is used for creating reports and data modeling?',
            options: [
              'Power BI Service',
              'Power BI Desktop',
              'Power BI Mobile',
              'Power BI Gateway'
            ],
            correctAnswer: 1,
            difficulty: 'easy',
            explanation: 'Power BI Desktop is the desktop application specifically designed for creating reports and performing data modeling tasks.',
            points: 1,
            type: 'multiple-choice'
          },
          {
            id: 'powerbi-intro-q2',
            question: 'What is the primary purpose of Power BI Service?',
            options: [
              'Creating complex data models',
              'Installing Power BI Desktop',
              'Sharing reports and collaboration',
              'Mobile app development'
            ],
            correctAnswer: 2,
            difficulty: 'easy',
            explanation: 'Power BI Service is the cloud-based platform primarily used for sharing reports, dashboards, and enabling collaboration among users.',
            points: 1,
            type: 'multiple-choice'
          },
          {
            id: 'powerbi-intro-q3',
            question: 'Which view in Power BI Desktop is best for creating visualizations?',
            options: [
              'Data view',
              'Model view',
              'Report view',
              'Query view'
            ],
            correctAnswer: 2,
            difficulty: 'medium',
            explanation: 'Report view is the primary interface for creating and arranging visualizations in Power BI Desktop.',
            points: 2,
            type: 'multiple-choice'
          },
          {
            id: 'powerbi-intro-q4',
            question: 'Power BI enables self-service analytics, which means:',
            options: [
              'Only IT professionals can create reports',
              'Business users can create their own reports without IT dependency',
              'Reports can only be created using code',
              'Data must be manually entered for each report'
            ],
            correctAnswer: 1,
            difficulty: 'medium',
            explanation: 'Self-service analytics means business users can independently create reports and analyze data without relying on IT departments.',
            points: 2,
            type: 'multiple-choice'
          },
          {
            id: 'powerbi-intro-q5',
            question: 'What is a key advantage of Power BI Mobile?',
            options: [
              'Creating complex data models',
              'Advanced report editing',
              'Touch-optimized interface for viewing reports',
              'Installing Power BI Desktop'
            ],
            correctAnswer: 2,
            difficulty: 'easy',
            explanation: 'Power BI Mobile provides a touch-optimized interface specifically designed for viewing and interacting with reports on mobile devices.',
            points: 1,
            type: 'multiple-choice'
          }
        ],
        passThreshold: 80
      }
    }
  ]
};
